import { TourAnchor } from "training/anchors";
import {
  all,
  eligibilityEnabled,
  hasSelfPayPayer,
  not,
  offersPatientEstimate,
  orderIsNoBalanceDue,
  paymentOptionsShown,
  usesPatientBalanceVariant,
} from "training/engine/conditions";
import { ITourStep } from "training/types";

import {
  benefitsDone,
  memberIdDone,
  patientDetailsDone,
  serviceAdded,
  visitDone,
} from "./gates";

const BENEFITS_HINT =
  "Fill in Remaining Deductible, Remaining Out-of-pocket Max and Coinsurance Percent to continue.";

/** The payer step's first two paragraphs (John, review 1); 2.5 adds the Self-Pay Rate one. */
const PAYER_TEXT = [
  "So far, we have dropped in demographic information and Date of Service information. Easy, right? Now we move onto the insurance validation — for this exercise we will use your insurance so you can see how this works in real time.",
  "This first dropdown within the Insurance and Benefits section will be pre-loaded with all of your facility's managed care contracts. It defaults alphabetically, so be sure to select the correct payer.",
];

/**
 * CLE-3015 Module 2: Creating and sending an estimate. Text is John's (review 1, 2026-10-23) where he gave
 * wording; the rest is the curriculum's (curriculum-v2, 2026-10-21), except the steps marked "Not curriculum
 * text": branch cards the curriculum describes but gives no trainee wording for (the Patient Balance variant,
 * the eligibility-failure explanation, no payment plans, the $0 / No Balance Due ending).
 *
 * Rows split so each element gets its own highlight: 2.7 (Member ID, then Run Eligibility) and 2.15 (the
 * footer, then Create and Send). Review 1 asked for 2.7 as one step (type the member ID, then Run
 * Eligibility moves the tour on); the engine cannot advance a `fill` step on a click on another element, so
 * it stays two steps, the first one with John's words.
 *
 * Review 1 removed "All changes saved" (m2.04w): the label is a UX problem to fix, not something to train.
 */
export const MODULE_2: ITourStep[] = [
  {
    id: "m2.00-before-you-start",
    module: 2,
    mode: "card",
    route: "estimates",
    text: "You are about to create a real estimate. Use your own name, your own cell number and your own email, so the text comes to you. For insurance, use your real plan if you are comfortable, or any made-up member ID; either way we run the check for real so you see what happens.",
  },
  {
    id: "m2.01-new-estimate",
    module: 2,
    anchor: TourAnchor.NewEstimate,
    label: "New Estimate",
    mode: "click",
    // The button navigates to /orders/new itself; the engine only hands the tour over.
    advanceOn: "route",
    route: "estimates",
    text: "Every estimate starts here.",
  },
  {
    id: "m2.02-order-type",
    module: 2,
    anchor: TourAnchor.OrderType,
    label: "Order Type",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    // The ER sentence and "selected for you" are review 1 (John: reference the ER type, and say Patient
    // Estimate is where it defaults).
    text: "Patient Estimate is the one you will use most: we check the patient's insurance and work out what they owe. It is already selected for you. Patient Balance is for a known amount, with no insurance check. Clear Package is a cash bundle price. ER is for an emergency room visit. You only see the types your hospital uses.",
  },
  {
    // Not curriculum text — the 2.2 branch: no Patient Estimate on this hospital.
    id: "m2.02v-order-type-patient-balance",
    module: 2,
    anchor: TourAnchor.OrderType,
    label: "Order Type",
    mode: "fill",
    route: "create",
    when: usesPatientBalanceVariant,
    text: 'Pick "Patient Balance". Your hospital does not use Patient Estimate, so this estimate is for a known amount the patient owes, with no insurance check. You only see the types your hospital uses.',
  },
  {
    id: "m2.03-patient-details",
    module: 2,
    anchor: TourAnchor.PatientDetails,
    // John: the heading says "Demographics", not "First name" (which is only the first field).
    title: "Demographics",
    mode: "fill",
    route: "create",
    done: patientDetailsDone,
    doneHint:
      "Fill in first name, last name, date of birth and a valid phone number to continue.",
    text: "Enter your own information here. Be sure to use a valid phone number, as this is where the text message will be sent. This is important to get right because this number is what we use to contact the patient. The system will automatically validate the number when you move to the next field. Email is optional, but if entered, the patient will also receive an email notification.",
  },
  {
    id: "m2.04-visit",
    module: 2,
    anchor: TourAnchor.VisitCard,
    label: "Visit",
    mode: "fill",
    route: "create",
    done: visitDone,
    doneHint:
      'Type an Account / Visit ID, and a date of service or tick "Not scheduled", to continue.',
    // Location shows only where the hospital manages estimates for several locations.
    text: [
      "Date of Service: For this exercise, just put the date of service about a week out. Best practice is to have estimates out to patients 2 weeks in advance to allow ample time for them to review and pay.",
      "Time of Service: If known, this will automatically let the patient know the time of their appointment as well. It is not a required field.",
      "Account / Visit ID: This is the patient's account number in your EHR; it labels the payment on your Tuesday reconciliation report. For this exercise, type TEST.",
      "Location: This will be enabled if you are an end user that manages estimates for multiple locations. For this exercise, just select any option.",
    ].join("\n"),
  },
  {
    // Review 1: "Not scheduled" gets its own step. The checkbox is in the Visit card's top right on every
    // order type, so it shows wherever the Visit card does. A look: the trainee has typed a date already.
    id: "m2.04n-not-scheduled",
    module: 2,
    anchor: TourAnchor.NotScheduled,
    label: "Not scheduled",
    mode: "look",
    route: "create",
    text: 'In the top right you will see a "Not scheduled" checkbox. This will allow you to send an estimate to a patient without a Date of Service. This is useful should you need to provide a price for an optional service, for example, that the patient has not elected to move forward with.',
  },
  {
    // Second run only: the trainee is their own patient, so a second estimate looks like a duplicate.
    id: "m2.04d-duplicate-warning",
    module: 2,
    anchor: TourAnchor.DuplicateWarning,
    mode: "look",
    route: "create",
    optional: true,
    waitMs: 1000,
    text: "The second time you make an estimate for the same patient, a warning says a possible duplicate was found. It will appear for a trainee who runs the tour twice.",
  },
  {
    id: "m2.05-payer",
    module: 2,
    anchor: TourAnchor.PayerName,
    label: "Payer Name",
    mode: "fill",
    route: "create",
    when: all(offersPatientEstimate, hasSelfPayPayer),
    text: [
      ...PAYER_TEXT,
      'This is also where we will load your Self-Pay Rates as a payer under "Self-Pay Rate". Use this option for a patient either not using or without insurance; it skips the eligibility check and uses your self-pay pricing.',
    ].join("\n"),
  },
  {
    id: "m2.05n-payer-no-self-pay",
    module: 2,
    anchor: TourAnchor.PayerName,
    label: "Payer Name",
    mode: "fill",
    route: "create",
    when: all(offersPatientEstimate, not(hasSelfPayPayer)),
    text: PAYER_TEXT.join("\n"),
  },
  {
    id: "m2.06-service-type",
    module: 2,
    anchor: TourAnchor.ServiceType,
    label: "Service Type",
    mode: "fill",
    route: "create",
    when: eligibilityEnabled,
    text: [
      "This tells the payer where the service happens, and as a result will change the benefits you get back. Picking the wrong one is the most common mistake we see, so we have built in logic to auto-select the likely choice for your facility. Best practice is ALWAYS to check the correct Site of Service is pre-selected.",
      'Note: If you are an ASC, you will always choose "ASC Facility", never "Hospital Outpatient".',
    ].join("\n"),
  },
  {
    id: "m2.07-member-id",
    module: 2,
    anchor: TourAnchor.MemberId,
    label: "Member ID",
    mode: "fill",
    route: "create",
    when: eligibilityEnabled,
    done: memberIdDone,
    doneHint: "Type a member ID to continue.",
    text: "This is where you enter the Member ID for the patient. For this exercise, simply type in the member ID that is on your own insurance card and click Next.",
  },
  {
    id: "m2.07b-run-eligibility",
    module: 2,
    anchor: TourAnchor.RunEligibility,
    label: "Run Eligibility",
    mode: "click",
    route: "create",
    when: eligibilityEnabled,
    text: "It usually takes a few seconds.",
  },
  {
    id: "m2.08-benefits",
    module: 2,
    anchor: TourAnchor.BenefitFields,
    label: "Remaining Deductible",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    done: benefitsDone,
    doneHint: BENEFITS_HINT,
    text: "This is where the patient stands with their plan today. The tabs on the right show each site of service the payer sent back; click a value there to use it. If the check failed, type a Remaining Deductible of 500, a Remaining Out-of-pocket Max of 5000 and a Coinsurance Percent of 20 so the estimate has a balance.",
  },
  {
    // Not curriculum text — the 2.7 branch: the check came back with a message instead of benefits.
    // The anchor is on the benefits area only while a message shows, so on success this step is skipped.
    id: "m2.08f-eligibility-message",
    module: 2,
    anchor: TourAnchor.EligibilityMessage,
    mode: "fill",
    route: "create",
    when: eligibilityEnabled,
    done: benefitsDone,
    doneHint: BENEFITS_HINT,
    optional: true,
    waitMs: 1500,
    text: 'The red message is what came back from the payer. "No eligibility response found", an "Eligibility Verification Error", coverage that shows inactive, or only out-of-network benefits all mean the check did not give us benefits to use. A made-up member ID lands here, and that is fine for this test: type a Remaining Deductible of 500, a Remaining Out-of-pocket Max of 5000 and a Coinsurance Percent of 20 so the estimate has a balance, then continue.',
  },
  {
    // Second run only.
    id: "m2.08p-previous-orders",
    module: 2,
    anchor: TourAnchor.PreviousOrders,
    mode: "look",
    route: "create",
    when: offersPatientEstimate,
    optional: true,
    waitMs: 1000,
    text: 'The second time you make an estimate for the same patient, the benefits card offers "Apply Reductions" to subtract what an earlier estimate already applied to the deductible.',
  },
  {
    id: "m2.09-add-secondary-insurance",
    module: 2,
    anchor: TourAnchor.AddSecondaryInsurance,
    label: "Add Secondary Insurance",
    mode: "look",
    route: "create",
    when: offersPatientEstimate,
    // Hidden when the payer is Self-Pay Rate; no need to wait the default 8 s for it.
    waitMs: 2000,
    text: "If the patient has a second plan, add it here and run eligibility again.",
  },
  {
    id: "m2.10-cpt-search",
    module: 2,
    anchor: TourAnchor.CptSearch,
    label: "Add CPT services",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    done: serviceAdded,
    doneHint: "Pick a code from the list to continue.",
    text: "Type a code or a word from the service name, such as MRI, and pick it. Add a second one if you like.",
  },
  {
    id: "m2.11-service-row",
    module: 2,
    anchor: TourAnchor.ServicesTable,
    label: "Description",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    // The table renders once a code is added and priced.
    waitMs: 15000,
    text: 'The price comes from your hospital\'s contract with this payer. A red mark on Amount means the price needs review. Units can be changed, for example for a drug code. "Multiple providers" means a bundle of facility and professional fees; expand it to see each.',
  },
  {
    // Only on hospitals with modality rules, and only when a rule fires.
    id: "m2.11n-service-type-changed",
    module: 2,
    anchor: TourAnchor.ServiceTypeChanged,
    mode: "look",
    route: "create",
    when: eligibilityEnabled,
    optional: true,
    waitMs: 1500,
    text: 'On some hospitals a notice titled "Service Type changed" appears after adding an MRI, CT, X-ray or ultrasound code, because a rule switched the service type to match. A "Keep ..." button puts it back.',
  },
  {
    // Not curriculum text — the Patient Balance variant's "a known balance".
    id: "m2.11v-patient-balance",
    module: 2,
    anchor: TourAnchor.PatientBalanceAmount,
    label: "Patient Balance",
    mode: "fill",
    route: "create",
    when: usesPatientBalanceVariant,
    text: 'Type the amount the patient owes in "Patient Balance". There is no insurance check on this type, so the amount you type is what the patient is asked to pay. For this test, use any amount above $0.',
  },
  {
    id: "m2.12-estimate-results",
    module: 2,
    anchor: TourAnchor.EstimateResults,
    label: "Estimate Results",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    text: [
      "Patient Responsibility is what the patient owes.",
      "Contracted Rate is what the payer allows.",
      "Insurance Pays is what the payer is paying towards this encounter.",
      "Open the Estimate breakdown drop down to see how deductible, coinsurance and copay add up. This is the math the patient will see on their estimate once it is sent to them as well. For this exercise, it must be more than $0; if it shows $0, go back and make sure the Remaining Out-of-pocket Max is 5000, not blank.",
    ].join("\n"),
  },
  {
    id: "m2.13-payment-options",
    module: 2,
    anchor: TourAnchor.PaymentOptions,
    label: "Offer a payment plan",
    mode: "fill",
    route: "create",
    when: paymentOptionsShown,
    text: 'Plans are on by default if your hospital offers them. Plans can be tiered based on dollar amounts. However, you also have the ability to alter the payment plan on each individual estimate if you choose. Feel free to explore the various options available and see how the Payment Plan breakdown at the bottom changes. Before you move on, keep it on "Set number of payments" along with the information already pre-populated so when you receive your text, you can see how the payment plan looks.',
  },
  {
    // Not curriculum text — the 2.13 branch: "Say plans are not set up for this hospital."
    id: "m2.13x-no-payment-plans",
    module: 2,
    mode: "card",
    route: "create",
    when: not(paymentOptionsShown),
    text: "Payment plans are not set up for your hospital, so there is no Payment Options card. The patient pays the full amount at checkout.",
  },
  {
    id: "m2.14-patient-communication",
    module: 2,
    anchor: TourAnchor.PatientCommunication,
    label: "Patient Communication",
    mode: "fill",
    route: "create",
    text: 'This will allow you to see the first text the patient will receive. These are set by default for your facility. If you click "Enabled" in the top right, it will toggle to "Disabled". Do this ONLY if the patient asked not to be contacted — nothing will be sent and you will collect in person. "Edit message" allows you to change the wording for this individual patient.',
  },
  {
    id: "m2.15-footer",
    module: 2,
    anchor: TourAnchor.SubmitFooter,
    label: "Save Draft",
    mode: "look",
    route: "create",
    text: [
      "You have 3 options to save this estimate:",
      "Save Draft keeps it for later under a status of 'Action Needed' and sends nothing.",
      "Schedule send picks a date and time for the first text. This is useful if staff is, for example, creating estimates at 5:30 AM and they want the texts to arrive to the patients at 9 AM that same morning.",
      "Create and Send sends the estimate out to the patient's phone immediately.",
    ].join("\n"),
  },
  {
    // The "Powered by Clear Health" badge used to sit over this button while it was highlighted; the tour
    // hides it while it runs (TOUR_HIDDEN_BADGE_CSS in components/TourPopover.tsx).
    id: "m2.15b-create-and-send",
    module: 2,
    anchor: TourAnchor.CreateAndSend,
    label: "Create and Send",
    mode: "click",
    // A click alone is not enough: the form may be invalid or the request may fail. The tour moves on
    // when the create page reports the estimate was created (and its id and status).
    advanceOn: "event",
    event: "order-created",
    route: "create",
    text: "To complete this exercise, go ahead and click Create and Send.",
  },
  {
    // A card, not the toast: the "Estimate Created!" toast lives about 3 seconds, outside any highlight,
    // and is gone before the hospital view has mounted and the tour has found it.
    id: "m2.16-estimate-created",
    module: 2,
    mode: "card",
    route: "estimates",
    text: "CONGRATULATIONS! You just sent your first estimate. Once you get the hang of it, it generally takes less than a minute to create and send these out. You should receive a text message shortly for the estimate you just created. This will allow you to see exactly what the patient sees when these are sent out. Once you receive the text, hit Next.",
  },
  {
    // Not curriculum text — the "2.12 and 2.16 Estimate at $0" branch. Later modules read
    // `trainingProgress.noBalanceDue` to skip 4.1–4.9 and 5.1. Before 2.17 (review 1), which ends the module.
    id: "m2.18-no-balance-due",
    module: 2,
    mode: "card",
    route: "estimates",
    when: orderIsNoBalanceDue,
    text: 'Your estimate came out at $0, so it was created as No Balance Due instead of Sent. Your phone got the "not expected to owe anything" text instead of a payment link. That is what a patient whose deductible is met, or who is on Medicaid, receives. There is nothing to collect on it, so the tour skips the payment steps that follow.',
  },
  {
    id: "m2.17-your-phone",
    module: 2,
    mode: "card",
    route: "estimates",
    continueLabel: "I've opened it, continue",
    text: [
      'Open the text and tap the link. To validate, enter your date of birth; this is to authenticate the patient. Now you see what the patient sees: what they are having done, what they owe and why, and how they can pay. If you see "Questions?", that is our chat assistant for patients. Feel free to interact with it, ask it questions about the estimate, etc. to see how patients can get questions answered without having to call.',
      "DO NOT PAY. Once you are done reviewing, come back to this screen and complete this module.",
    ].join("\n"),
  },
];
